import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { forecastService } from '@/services/demandPlanning/forecast.service';

import { ForecastInboxActionModal } from '../components/ForecastInboxActionModal';
import { demandPlanningPageClass } from '../utils/pageShell';
import type { ForecastAnomaly } from '../types/forecast.types';

const RUN_STORAGE_KEY = 'dp-forecast-selected-run';

function readStoredRunId(): number | null {
  try {
    const raw = localStorage.getItem(RUN_STORAGE_KEY);
    if (!raw) return null;
    const id = Number.parseInt(raw, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function ForecastAnomaliesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [runId, setRunId] = useState<number | null>(readStoredRunId);
  const [modalAlert, setModalAlert] = useState<ForecastAnomaly | null>(null);
  const [modalMode, setModalMode] = useState<'approve' | 'dismiss' | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const runs = useQuery({
    queryKey: ['forecast-runs-list'],
    queryFn: () => forecastService.listRuns(),
  });

  const runRows = runs.data ?? [];

  useEffect(() => {
    if (!runRows.length) return;
    if (runId != null && runRows.some((r) => r.id === runId)) return;
    setRunId(runRows[0].id);
  }, [runRows, runId]);

  const anomalies = useQuery({
    queryKey: ['forecast-anomalies', runId],
    queryFn: () => forecastService.getAnomalies(runId!, 100),
    enabled: runId != null,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['forecast-anomalies'] });
    void queryClient.invalidateQueries({ queryKey: ['forecast-run-anomalies'] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) =>
      forecastService.applyAlertRecommendation(id, note),
    onMutate: ({ id }) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: () => {
      invalidate();
      setModalMode(null);
      setModalAlert(null);
    },
  });

  const dismissMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) =>
      forecastService.dismissAlert(id, note),
    onMutate: ({ id }) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: () => {
      invalidate();
      setModalMode(null);
      setModalAlert(null);
    },
  });

  const renderList = (items: ForecastAnomaly[], emptyKey: string) => {
    if (!items.length) {
      return <p className="py-4 text-sm text-gray-500">{t(emptyKey)}</p>;
    }
    return (
      <ul className="divide-y divide-gray-100">
        {items.map((alert) => (
          <li key={alert.id} className="py-3 text-sm">
            <div className="font-medium text-gray-900">{alert.title}</div>
            <p className="text-xs text-gray-600 mt-0.5">{alert.message}</p>
            <p className="text-[10px] text-gray-400 mt-1">
              {alert.status} · {alert.scope_type} {alert.scope_code ?? ''}
            </p>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={`${demandPlanningPageClass()} pt-4 md:pt-5`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('forecastPage.anomaliesPage.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{t('forecastPage.anomaliesPage.subtitle')}</p>
        </div>
        <Link to="/demand-planning/forecast" className="text-sm text-stocker-primary hover:underline">
          ← {t('forecastPage.title')}
        </Link>
      </div>

      <label className="flex flex-col gap-1 text-sm max-w-md">
        <span className="font-medium text-gray-700">{t('forecastPage.forecastRun')}</span>
        <select
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={runId ?? ''}
          onChange={(e) => setRunId(Number(e.target.value))}
        >
          {runRows.map((run) => (
            <option key={run.id} value={run.id}>
              #{run.id} · {run.model_version}
            </option>
          ))}
        </select>
      </label>

      {anomalies.isLoading ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <>
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">
              {t('forecastPage.anomaliesPage.active')} ({anomalies.data?.active.length ?? 0})
            </h2>
            {renderList(anomalies.data?.active ?? [], 'forecastPage.workInbox.empty')}
          </section>
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">
              {t('forecastPage.anomaliesPage.history')} ({anomalies.data?.history.length ?? 0})
            </h2>
            {renderList(
              anomalies.data?.history ?? [],
              'forecastPage.anomaliesPage.noHistory',
            )}
          </section>
        </>
      )}

      <ForecastInboxActionModal
        open={modalMode != null && modalAlert != null}
        mode={modalMode ?? 'approve'}
        alert={modalAlert}
        busy={busyId != null}
        onClose={() => {
          setModalMode(null);
          setModalAlert(null);
        }}
        onConfirm={(note) => {
          if (!modalAlert || !modalMode) return;
          if (modalMode === 'approve') {
            approveMutation.mutate({ id: modalAlert.id, note });
          } else {
            dismissMutation.mutate({ id: modalAlert.id, note });
          }
        }}
      />
    </div>
  );
}
